import { formatCurrency, toPennies } from '../utils';
import productUtils from './productUtils';
import { getProductsBySku } from '../actions/home';

let cartUtils = {
    getName(cart, sopSub) {
        let hash = cart._id.substring(cart._id.length - 4, cart._id.length);
        let header = cart.name || `Cart ${hash}`;

        if (cart.is_pif) {
            header = 'Play It Forward';
        }

        if (cart.sop_id) {
            header = sopSub ? `${sopSub.description} - ${sopSub.quantity}` : `SOP ${cart.sop_id}`;
        }

        if (cart.is_wishlist) {
            header = cart.name || `Unknown ${cart._id}`;
        }

        if (cart.is_primary) header = 'Primary Cart';

        return header;
    },

    setupCart(cart) {
        let skus = cart.items.map((item) => item.sku);
        return getProductsBySku(skus);
    },

    addItem(products, newItem) {
        let existing = products.find((item) => item.sku === newItem.sku);

        if (existing) {
            existing.qty = existing.qty + newItem.qty;
            return;
        }

        products.push(newItem);
    },

    removeItem(products, item) {
        return products.filter((xItem) => xItem.sku !== item.sku);
    },

    addMultipleItems(products, newItems = []) {
        newItems.forEach((item) => {
            this.addItem(products, item);
        });
    },

    cleanAddress(address) {
        let { street, region, city, country, phone, postal_code, to, attn } = address;
        let data = { street, region, city, country, phone, postal_code, to, attn };

        for (var prop in data) {
            if (data[prop] === null || data[prop] === undefined) {
                delete data[prop];
            }
        }

        return data;
    },

    // TODO: Rename `getItemStubs`
    getItemsStub(products) {
        return products.map((item) => {
            return {
                sku: item.sku,
                qty: parseInt(item.qty, 10),
                allocations: item.allocations,
                include_warranty: item.include_warranty,
                pkg_type: item.pkg_type
            };
        });
    },

    getUnitCount(products, includeServices = false) {
        let count = 0;
        let items = includeServices
            ? products
            : products.filter((item) => !item.format || !item.format.includes('service'));
        items.forEach((item) => {
            count = count + parseInt(item.qty, 10);
        });
        return count;
    },

    // TODO: Rename `getSubTotal`
    getSubtotal(products, currency = 'USD') {
        let total = 0.0;
        products.forEach((item) => {
            total = total + toPennies(productUtils.getListPrice(item, currency)) * parseInt(item.qty, 10);
        });
        return total;
    },

    getGrandTotal(products, priceContracts = {}, incShipping = false, currency = 'USD') {
        let total = 0;
        let shipping = this.getShipping(products);

        products.forEach((item) => {
            let discount = productUtils.getContractDiscount(item, priceContracts) || 0;
            let itemTotal = productUtils.getTotal(item, discount, currency);
            total = total + toPennies(itemTotal);
        });

        if (incShipping) {
            total = total + shipping;
        }

        return total;
    },

    getDiscount(products, priceContracts, currency = 'USD') {
        let subTotal = this.getSubtotal(products, currency);
        let grandTotal = this.getGrandTotal(products, priceContracts, false, currency);
        return subTotal - grandTotal;
    },

    getShipping(products, isPif, currency = 'USD') {
        if (isPif) {
            return 0;
        }
        let subTotal = this.getSubtotal(products, currency);
        let total = 0;
        // 10000 == 100.00
        if (subTotal < 10000 && subTotal > 0) {
            total = 695;
        }
        return total;
    },

    getPercentComplete(cart, max = 100) {
        var qty = this.isPromoMonth(cart) ? cart.quantity * this.getPromoMultiplier(cart) : cart.quantity;
        var percent = Math.round((this.getUnitCount(cart.items) / qty) * 100);

        if (percent > max) {
            return max;
        }

        return percent;
    },

    getChargedItems: (items, limit) => {
        let count = 0;
        let includeItems = [];
        items.forEach((item) => {
            let nextCount = count + item.qty;
            // bail if we already met our limit.
            if (count === limit) return false;

            // if we are within our limit add the item to our new list.
            if (nextCount <= limit) {
                count = nextCount;
                includeItems.push(item);
                return false;
            }

            // if the current item qty will exceed our limit
            // but we havent met our limit yet, subtract the difference
            // and add the item to our new list.
            if (nextCount > limit && count !== limit) {
                let difference = nextCount - limit;
                count = nextCount - difference;
                includeItems.push({
                    ...item,
                    qty: item.qty - difference
                });
                return false;
            }
        });
        return includeItems;
    },

    validateAllocations(items) {
        let isValid = true;

        items.forEach((item) => {
            var allocTotal = 0;

            if (!item.allocations || !item.allocations.length) {
                isValid = isValid && true;
                return;
            }

            item.allocations
                .filter((a) => a)
                .forEach((line) => {
                    allocTotal += parseInt(line.qty || 0, 10);
                });

            isValid = isValid && allocTotal === item.qty;
        });
        return isValid;
    },

    getPromoMultiplier(sub) {
        if (!sub) return 1;
        var promo = sub.promo ? sub.promo.promotype || sub.promo.type : null;
        if (!promo) {
            return 1;
        }

        promo = promo.toLowerCase();

        if (promo.indexOf('double') === 0) {
            return 2;
        }

        if (promo.indexOf('triple') === 0) {
            return 3;
        }

        return 1;
    },

    isPromoMonth(sub, current = new Date()) {
        if (!sub || !sub.promo) {
            return false;
        }
        let month = sub.promo.promomonth || sub.promo.month;
        let p = new Date(`${month.split(/T|\s/g)[0]}T00:00:00`);

        return p.getFullYear() === current.getFullYear() && p.getMonth() === current.getMonth();
    },

    isSopComplete(sub, items) {
        var qty = this.isPromoMonth(sub) ? sub.quantity * this.getPromoMultiplier(sub) : sub.quantity;
        var percent = (sub.quantity / qty) * 100;
        return this.getPercentComplete({ ...sub, items }) >= percent;
    },

    createMarcAlloc(alloc) {
        let keyMap = {
            qty: 'Qty',
            branch: 'Branch',
            collection: 'Collection',
            fund: 'Fund',
            holding_code: 'Holdingcodes',
            item_type: 'Itemtype',
            location: 'Location'
        };
        let newAlloc = {};
        Object.keys(alloc).forEach((key) => {
            if (alloc[key]) {
                newAlloc[keyMap[key]] = alloc[key];
            }
        });
        return newAlloc;
    },

    getSopMonth(asText = false, date = new Date()) {
        let month = date.getMonth() + 1; // date.getMonth() is zero indexed.
        month = month < 12 ? month : 0;

        let mL = [
            'January',
            'February',
            'March',
            'April',
            'May',
            'June',
            'July',
            'August',
            'September',
            'October',
            'November',
            'December'
        ];

        if (asText) {
            return mL[month];
        }

        return month + 1; // zero index so we get the correct month integer.
    },

    getSopYear(date = new Date()) {
        let month = this.getSopMonth(false, date); // date.getMonth() is zero indexed.
        const year = date.getFullYear();
        return month === 1 ? year + 1 : year;
    },

    createOrderObject(cart, account, billing, shipping, notes = '') {
        return {
            cart_id: cart._id,
            shipping_address: cartUtils.cleanAddress(shipping),
            billing_address: cartUtils.cleanAddress(billing),
            currency: account.org.currency,
            customer_id: account.info._id,
            payment: {},
            is_pif: cart.is_pif,
            items: cart.items.map((item) => {
                let discount = productUtils.getContractDiscount(item, account.org.price_contracts);
                return productUtils.getOrderStub(item, discount, account.org.currency);
            }),
            promos_applied: cart.promos_applied,
            netsuite_entity: account.info.netsuite_entity,
            sub_total: formatCurrency(cartUtils.getSubtotal(cart.items)),
            discount: formatCurrency(cartUtils.getDiscount(cart.items, account.org.price_contracts)),
            shipping: formatCurrency(cartUtils.getShipping(cart.items, cart.is_pif, account.org.currency)),
            grand_total: formatCurrency(cartUtils.getGrandTotal(cart.items, account.org.price_contracts, true)),
            notes: notes
        };
    },

    createMarcRequestObject(order, account, org, date = new Date()) {
        let priceContracts = org.price_contracts;
        // filter out services, warranties, and accessories.
        let items = order.items.filter(
            (i) =>
                i.format &&
                !i.format.includes('service') &&
                !i.format.includes('warranty') &&
                !i.format.includes('accessory')
        );
        let services = order.items.filter((i) => i.format && i.format.includes('service'));
        let warranties = order.items.filter((i) => i.format && i.format.includes('warranty'));

        return {
            sopId: order.sop_id,
            sopMonth: order.sop_id ? this.getSopMonth(false, date) : null,
            sopYear: order.sop_id ? this.getSopYear(date) : null,
            shipToAddress: order.shipping_address,
            billToAddress: order.billing_address,
            promoCode: order.promos_applied && order.promos_applied.length ? order.promos_applied[0] : null,
            isPif: order.is_pif,
            Items: items.map((item) => {
                let pcDiscount = productUtils.getContractDiscount(item, priceContracts);
                let discount = item.discount ? item.discount : pcDiscount;
                let itemQty = parseInt(item.qty, 10);

                let listPrice = productUtils.getListPrice(item, org.currency);
                let itemType = item.format === 'recorded-books' ? 'playaway' : item.format; // Force RB titles to lookup services and warranties as Playaway

                let service = services.find((i) => i.format === `service-${itemType}`);
                let servicePrice = service ? productUtils.getFinalPrice(service, 0, org.currency) : 0;

                let warranty = warranties.find((i) => i.format === `warranty-${itemType}`);
                let warrantyPrice =
                    warranty && item.include_warranty ? productUtils.getFinalPrice(warranty, 0, org.currency) : 0;

                let finalPrice =
                    productUtils.getFinalPrice(item, discount, org.currency) + servicePrice + warrantyPrice;

                let total =
                    productUtils.getTotal(item, discount, org.currency) +
                    servicePrice * itemQty +
                    warrantyPrice * itemQty;
                return {
                    Sku: item.sku,
                    Qty: itemQty,
                    UnitPrice: formatCurrency(finalPrice),
                    Discount: item.discount,
                    DiscountPercent: discount,
                    ContractDiscount: formatCurrency(toPennies(listPrice) * discount),
                    Total: formatCurrency(total),
                    CustomerId: account.email,
                    OrganizationId: org.entity_id,
                    PONumber: order.payment.po_number || '',
                    PODate: order.submitted_date || date.toISOString().split('T')[0],
                    Notes: order.notes || '',
                    Attn: order.shipping_address.attn,
                    VendorId: 'FIND',
                    Allocations: item.allocations
                        ? item.allocations.filter((a) => a).map((alloc) => this.createMarcAlloc(alloc))
                        : [],
                    ServiceKitInternalId: service ? service.internalid : null,
                    ServiceKitUnitPrice: formatCurrency(servicePrice),
                    ExtendedWarrantyInternalId: warranty && item.include_warranty ? warranty.internalid : null,
                    ExtendedWarrantyUnitPrice: formatCurrency(warrantyPrice)
                };
            })
        };
    },
    getUrl(cart, account_id) {
        if (!cart) return '';
        return `/cart/${cart._id}`;
    }
};

export default cartUtils;
